import { useCallback, useEffect, useMemo, useState } from 'react';

import { showErrorMessage } from '@/shared/components/toast-system';
import { getFirmNameListOptions, getUsageSummary, searchUsage } from '../../../dao/BillingMetricDAO';
import { OPERATOR_AND, OPERATOR_EQUAL, OPERATOR_GREATERTHAN_EQUAL, OPERATOR_LESSTHAN_EQUAL } from '../../../dao/dbConstants';

export const PAGE_SIZE = 25;
const EMPTY_FILTERS = { from: '', to: '', firmId: '', feature: '', aiModel: '' };

const startOfDay = (date) => new Date(`${date}T00:00:00`).toISOString();
const endOfDay = (date) => new Date(`${date}T23:59:59.999`).toISOString();

const toSummaryFilters = (filters) => ({
  from: filters.from ? startOfDay(filters.from) : undefined,
  to: filters.to ? endOfDay(filters.to) : undefined,
  firmId: filters.firmId || undefined,
  feature: filters.feature || undefined,
  aiModel: filters.aiModel || undefined,
});

const toCriteria = (filters) => [
  filters.from && { fieldName: 'createdOn', operator: OPERATOR_GREATERTHAN_EQUAL, value: startOfDay(filters.from) },
  filters.to && { fieldName: 'createdOn', operator: OPERATOR_LESSTHAN_EQUAL, value: endOfDay(filters.to) },
  filters.firmId && { fieldName: 'firmId', operator: OPERATOR_EQUAL, value: filters.firmId },
  filters.feature && { fieldName: 'feature', operator: OPERATOR_EQUAL, value: filters.feature },
  filters.aiModel && { fieldName: 'aiModel', operator: OPERATOR_EQUAL, value: filters.aiModel },
].filter(Boolean);

const toOptions = (rows = []) => rows.filter((row) => row.key).map((row) => ({ value: row.key, label: row.key }));

const useUsageData = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ orderBy: 'createdOn', sortDirection: 'desc' });
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [facets, setFacets] = useState({ features: [], models: [], firms: [] });

  // The filter dropdowns come from the unfiltered totals, so picking one value does not hide the others.
  useEffect(() => {
    const loadFacets = async () => {
      try {
        const [unfiltered, firms] = await Promise.all([getUsageSummary({}), getFirmNameListOptions()]);
        setFacets({
          features: toOptions(unfiltered.byFeature),
          models: toOptions(unfiltered.byModel),
          firms: firms.map((firm) => ({ value: String(firm.value), label: firm.label })),
        });
      } catch (error) {
        console.error('useUsageData: failed to load filter options', error);
      }
    };
    loadFacets();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const [nextSummary, list] = await Promise.all([
          getUsageSummary(toSummaryFilters(filters)),
          searchUsage({
            pageNumber: page,
            pageSize: PAGE_SIZE,
            orderBy: sort.orderBy,
            sortDirection: sort.sortDirection,
            group: { operator: OPERATOR_AND, criteria: toCriteria(filters) },
          }),
        ]);
        if (cancelled) return;
        setSummary(nextSummary);
        setRows(list.list ?? []);
        setTotalCount(list.totalCount ?? 0);
      } catch (error) {
        if (cancelled) return;
        console.error('useUsageData: failed to load usage', error);
        showErrorMessage('Failed to load usage data.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters, page, sort]);

  const updateFilter = useCallback((name, value) => {
    setFilters((current) => ({ ...current, [name]: value ?? '' }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const toggleSort = useCallback((orderBy) => {
    setSort((current) => ({
      orderBy,
      sortDirection: current.orderBy === orderBy && current.sortDirection === 'desc' ? 'asc' : 'desc',
    }));
    setPage(1);
  }, []);

  const hasFilters = useMemo(() => Object.values(filters).some(Boolean), [filters]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return {
    filters, updateFilter, clearFilters, hasFilters, facets,
    summary, rows, totalCount, isLoading,
    page, setPage, totalPages, sort, toggleSort,
  };
};

export default useUsageData;
